import { useRef } from "react";
import { X } from "lucide-react";
import frontImg from "@/assets/bodymap/front.png";
import backImg from "@/assets/bodymap/back.png";

// Front/back anatomical body chart. Tapping drops a numbered pin at the tap
// location (stored as x/y percentages + which view). Fully offline-safe: the
// figures are bundled assets and pins are plain JSON kept on the injury row.

const VIEWS = [["front", "Front", frontImg], ["back", "Back", backImg]];

export function BodyMap({ value = [], onChange, testid }) {
  const wrap = useRef(null);
  const pins = Array.isArray(value) ? value : [];

  const addPin = (v, e) => {
    const el = e.currentTarget.getBoundingClientRect();
    const x = +(((e.clientX - el.left) / el.width) * 100).toFixed(1);
    const y = +(((e.clientY - el.top) / el.height) * 100).toFixed(1);
    onChange([...pins, { x, y, view: v }]);
  };

  const removePin = (i) => onChange(pins.filter((_, idx) => idx !== i));

  const Figure = ({ v, img }) => {
    const vp = pins.map((p, i) => ({ ...p, i })).filter((p) => p.view === v);
    return (
      <div className="relative flex-1" data-testid={`${testid}-${v}`}>
        <div className="text-[10px] text-slate-400 text-center mb-1 uppercase tracking-wide">{v}</div>
        <div
          className="relative rounded-lg bg-black/30 border border-white/10 overflow-hidden cursor-crosshair"
          onClick={(e) => addPin(v, e)}
          style={{ aspectRatio: "848 / 1264" }}
        >
          <img src={img} alt={`${v} body view`} draggable={false}
            className="w-full h-full object-contain pointer-events-none select-none p-1.5" />
          {vp.map((p) => (
            <button
              key={p.i}
              type="button"
              onClick={(e) => { e.stopPropagation(); removePin(p.i); }}
              data-testid={`${testid}-pin-${p.i}`}
              title="Tap to remove"
              className="absolute -translate-x-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-red-500 border-2 border-white text-white text-[9px] font-bold flex items-center justify-center shadow"
              style={{ left: `${p.x}%`, top: `${p.y}%` }}
            >
              {p.i + 1}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div data-testid={testid} ref={wrap}>
      <div className="text-[10px] text-slate-400 mb-1.5">Tap the body to mark injury locations · tap a pin to remove it</div>
      <div className="flex gap-3">
        {VIEWS.map(([v, , img]) => <Figure key={v} v={v} img={img} />)}
      </div>
      {pins.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testid}-list`}>
          {pins.map((p, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-200 bg-red-500/15 border border-red-500/30 rounded-full px-2 py-0.5">
              #{i + 1} · {p.view}
              <button type="button" onClick={() => removePin(i)} data-testid={`${testid}-remove-${i}`} className="hover:text-white"><X className="h-2.5 w-2.5" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
